import * as R from "ramda";
import React, { useRef } from "react";
import { showModal, hideModal } from "redux/modules/modal/actions";
import { connect } from "react-redux";
import { bindInstance } from "redux-mvc";

import StatusCircle from "ui-kit/StatusCircle";
import Tooltip from "components/Global/Tooltip";

import * as STANDARD_MODULE_IDS from "@lennd/value-types/src/constants/standard-modules";
import { FORM_STATUS } from "../../constants";

import OrderModal from "Orders/OrderModal/View";
import AssignAccountFormsModal from "components/Global/CRM/Modals/Assign/AssignForms/AssignAccountForms";
import AssignContactFormsModal from "components/Global/CRM/Modals/Assign/AssignForms/AssignContactForms";

import ModalWrapper from "components/Global/Modal/Wrappers/Black";

import { getValue, getSettings } from "../../utils";
import { actions } from "../../model";
import { noop, parseComboId } from "utils/General";
import { MenuItem } from "components/Base/Level2/Cards/MenuCards";
import MenuPopover from "ui-kit/MenuPopover";
import { Div } from "components/Base";
import moment from "moment";
import SendInductionFormModal from "EventLight/Common/SendInductionFormModal";
import OverrideInductionFormModal from "EventLight/Common/OverrideInductionFormModal";
import { eventId as getEventId } from "redux/modules/event/selectors";

const decorate = connect(
  (state, ownProps) => ({
    // Map handlers from parent component if they exist
    eventId: getEventId(state),
    handlers: ownProps.handlers || {}
  }),
  bindInstance({
    showModal,
    hideModal,
    onDone: actions.updateData
  })
);

// Status button styles
const statusButtonStyles = {
  base: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: "3px",
    color: "white",
    fontWeight: "600",
    cursor: "pointer",
    height: "30px",
    width: "110px",
    border: "none",
    justifyContent: "space-between",
    fontSize: "14px",
    margin: "4px 0",
    gap: "5px",
    fontFamily: "Nunito !important"
  },
  [FORM_STATUS.OVERRIDDEN]: {
    backgroundColor: "#20CCD2"
  },
  [FORM_STATUS.PENDING]: {
    backgroundColor: "#F69229"
  },
  [FORM_STATUS.SEND_FORM]: {
    backgroundColor: "#9F86D7"
  },
  [FORM_STATUS.COMPLETED]: {
    backgroundColor: "#20D293"
  },
  [FORM_STATUS.NOT_SENT]: {
    backgroundColor: "#c7c7c7"
  }
};

// Format a date string to the format (1/31/25 - 9:15AM)
const formatSentTime = dateString => {
  if (!dateString) return "N/A";
  const date = moment(dateString);
  if (!date.isValid()) return "N/A";
  return date.format("M/D/YY - h:mm A");
};

const Form = props => {
  const buttonRef = useRef(null);
  const contactId = props.data.customer_contact_id ?? props.data.id;

  const showSubmissionModal = () => {
    const recordId = getValue(props, "recordId");

    props.showModal({
      content: (
        <OrderModal submissionId={recordId} onDone={() => props.onDone()} />
      ),
      wrapper: ModalWrapper
    });
  };

  const showAssignFormModal = () => {
    const moduleId = getSettings(props, "moduleId");

    const Modal =
      moduleId === STANDARD_MODULE_IDS.accounts.id
        ? AssignAccountFormsModal
        : AssignContactFormsModal;

    props.showModal({
      content: (
        <Modal
          moduleId={moduleId}
          recordNameSingular={
            moduleId === STANDARD_MODULE_IDS.accounts.id ? "Group" : "Person"
          }
          recordNamePlural={
            moduleId === STANDARD_MODULE_IDS.accounts.id ? "Groups" : "People"
          }
          formIds={[R.path(["field", "id"], props)]}
          recordIds={[parseComboId(props.data.id)[0]]}
          onDone={() => {
            props.hideModal();
            props.onDone();
          }}
        />
      ),
      wrapper: ModalWrapper
    });
  };

  const sendInductionForm = () => {
    if (contactId) {
      props.showModal({
        content: (
          <SendInductionFormModal
            selectedContactIds={[contactId]}
            closeModal={props.hideModal}
            eventId={props.eventId}
            fetchData={() => props.onDone()}
            sendTo={props.data?.full_name?.value || ""}
          />
        ),
        wrapper: ModalWrapper
      });
    }
  };

  const resendInductionForm = () => {
    if (contactId) {
      props.showModal({
        content: (
          <SendInductionFormModal
            selectedContactIds={[contactId]}
            closeModal={props.hideModal}
            eventId={props.eventId}
            fetchData={() => props.onDone()}
            resendTo={props.data?.full_name?.value || ""}
          />
        ),
        wrapper: ModalWrapper
      });
    }
  };

  const handleOverrideForm = () => {
    if (contactId) {
      props.showModal({
        content: (
          <OverrideInductionFormModal
            contactIds={[contactId]}
            closeModal={props.hideModal}
            eventId={props.eventId}
            fetchData={() => props.onDone()}
            contactName={props.data?.full_name?.value || ""}
          />
        ),
        wrapper: ModalWrapper
      });
    }
  };

  const hasInductionForm = (rowData, columnId) => {
    const formCell = rowData[columnId];
    const isInduction = formCell?.type === "form" && formCell?.isInduction;
    return isInduction ? formCell?.value : null;
  };

  const renderStatusButton = (inductionStatus, sentTime) => {
    const status =
      typeof inductionStatus === "string" ? inductionStatus.toLowerCase() : "";

    const isFieldReadOnly = getSettings(props, "readOnly", false);

    let buttonProps = {
      style: { ...statusButtonStyles.base }
    };

    // Render different buttons based on inductionStatus
    switch (status) {
      case FORM_STATUS.OVERRIDDEN:
        return (
          <button
            {...buttonProps}
            style={{
              ...buttonProps.style,
              ...statusButtonStyles[FORM_STATUS.OVERRIDDEN]
            }}
          >
            <img src="/img/warning-icon.svg" alt="icon" />
            <span>Overridden</span>
          </button>
        );
      case FORM_STATUS.PENDING:
        return (
          <MenuPopover
            ref={buttonRef}
            Label={
              <button
                id="status-button"
                {...buttonProps}
                style={{
                  ...buttonProps.style,
                  ...statusButtonStyles[FORM_STATUS.PENDING],
                  position: "relative"
                }}
              >
                <img src="/img/clock.svg" alt="clock" />
                <span style={{ pointerEvents: "none" }}>Pending</span>
                <img
                  src="/img/dropdown-shape-1.svg"
                  alt="icon"
                  style={{ pointerEvents: "none" }}
                />
              </button>
            }
            elevation={3}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
            PaperProps={{
              style: {
                marginTop: 4,
                minWidth: "150px"
              }
            }}
            wrapperStyle={{
              position: "relative",
              display: "block",
              width: "100%"
            }}
          >
            {({ closePopover }) => (
              <Div ref={buttonRef.current}>
                <MenuItem
                  LeftIcon={null}
                  RightIcon={null}
                  onClick={noop}
                  style={{
                    minHeight: "50px",
                    paddingTop: "8px",
                    pointerEvents: "none",
                    backgroundColor: "#FFF1CA"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px"
                    }}
                  >
                    <span>Sent</span>
                    <span>{formatSentTime(sentTime)}</span>
                  </div>
                </MenuItem>
                <MenuItem
                  LeftIcon={null}
                  RightIcon={null}
                  onClick={() => {
                    closePopover();
                    resendInductionForm();
                  }}
                >
                  <span>Re-send</span>
                </MenuItem>
                <MenuItem
                  LeftIcon={null}
                  RightIcon={null}
                  onClick={() => {
                    closePopover();
                    handleOverrideForm();
                  }}
                >
                  <span>Override</span>
                </MenuItem>
              </Div>
            )}
          </MenuPopover>
        );
      case FORM_STATUS.NOT_SENT:
        return (
          <button
            {...buttonProps}
            style={{
              ...buttonProps.style,
              ...statusButtonStyles[FORM_STATUS.SEND_FORM]
            }}
            onClick={sendInductionForm}
          >
            <img src="/img/mail-icon.svg" alt="clock" />
            <span>Send Form</span>
          </button>
        );
      case FORM_STATUS.COMPLETED:
        return (
          <button
            {...buttonProps}
            style={{
              ...buttonProps.style,
              ...statusButtonStyles[FORM_STATUS.COMPLETED]
            }}
          >
            <img src="/img/correct-icon.svg" alt="check" />
            <span>Completed</span>
          </button>
        );
      default:
        // Default to the original status circle for other statuses
        const status = getValue(props, "status", "");
        const statusMap = {
          [FORM_STATUS.NOT_SUBMITTED]: {
            status: FORM_STATUS.NOT_SUBMITTED,
            tooltip: "Not Submitted"
          },
          [FORM_STATUS.PENDING]: {
            status: FORM_STATUS.PENDING,
            tooltip: "Pending Approval"
          },
          [FORM_STATUS.APPROVED]: {
            status: FORM_STATUS.APPROVED,
            tooltip: "Approved"
          },
          [FORM_STATUS.REJECTED]: {
            status: FORM_STATUS.REJECTED,
            tooltip: "Rejected"
          },
          default: {
            status: FORM_STATUS.NOT_ASSIGNED,
            tooltip: "Not Assigned"
          }
        };

        return (
          <div
            onClick={
              isFieldReadOnly
                ? noop
                : [
                    FORM_STATUS.PENDING,
                    FORM_STATUS.APPROVED,
                    FORM_STATUS.REJECTED
                  ].includes(status)
                ? showSubmissionModal
                : showAssignFormModal
            }
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
              height: "100%",
              padding: 8,
              borderRadius: 4,
              cursor: isFieldReadOnly ? "default" : "pointer"
            }}
          >
            <Tooltip
              tooltip={R.pathOr(
                statusMap.default.tooltip,
                [status, "tooltip"],
                statusMap
              )}
              placement="bottom"
            >
              <span>
                <StatusCircle
                  status={R.pathOr(
                    statusMap.default.status,
                    [status, "status"],
                    statusMap
                  )}
                  onClick={noop}
                />
              </span>
            </Tooltip>
          </div>
        );
    }
  };

  const inductionForm = hasInductionForm(props.data, props.field.id);

  if (inductionForm) {
    const { status: inductionStatus, emailSentAt } = inductionForm;
    return renderStatusButton(
      inductionStatus || FORM_STATUS.NOT_SENT,
      emailSentAt
    );
  }

  return renderStatusButton();
};

export default decorate(Form);
